import { useEffect, useState } from "react";
import { Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { OFFER_ENDS } from "@/config/pricing";

interface Schedule {
  code: string;
  label: string | null;
  starts_at: string | null;
  ends_at: string | null;
  active: boolean;
}

interface Props {
  className?: string;
  compact?: boolean;
}

function diffParts(target: Date) {
  const now = new Date();
  const ms = target.getTime() - now.getTime();
  if (ms <= 0) return null;
  const totalSec = Math.floor(ms / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  return { days, hours, minutes, seconds };
}

export default function LaunchOfferCountdown({ className, compact = false }: Props) {
  const [schedule, setSchedule] = useState<Schedule | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.rpc("get_launch_offer_schedule");
      if (!cancelled && !error && Array.isArray(data) && data[0]) {
        setSchedule(data[0] as Schedule);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // No admin-scheduled coupon window (or it is switched off) — fall back to the
  // canonical launch offer end date so the buyer always sees one
  // deadline, and only one.
  const usable = schedule && schedule.active && schedule.ends_at ? schedule : null;

  const target = usable ? new Date(usable.ends_at!) : OFFER_ENDS;
  const startsAt = usable?.starts_at ? new Date(usable.starts_at) : null;
  if (startsAt && startsAt.getTime() > now) return null;

  const parts = diffParts(target);
  if (!parts) return null;

  const endLabel = target.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  if (compact) {
    return (
      <div
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-1 text-[11px] font-semibold text-primary",
          className,
        )}
        aria-live="polite"
      >
        <Clock className="h-3 w-3" />
        Ends in {parts.days}d {parts.hours}h {parts.minutes}m
      </div>
    );
  }

  return (
    <div
      className={cn(
        "inline-flex flex-col items-center gap-2 rounded-2xl border border-primary/30 bg-primary/5 px-4 py-3",
        className,
      )}
      aria-live="polite"
      aria-label={`Launch offer ends on ${endLabel}`}
    >
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-primary">
        <Clock className="h-3 w-3" /> Launch offer ends in
      </div>
      <div className="flex items-baseline gap-2 font-display tabular-nums text-foreground">
        <TimeBox value={parts.days} label="days" />
        <span className="text-xl font-bold text-primary/40">:</span>
        <TimeBox value={parts.hours} label="hrs" />
        <span className="text-xl font-bold text-primary/40">:</span>
        <TimeBox value={parts.minutes} label="min" />
        <span className="text-xl font-bold text-primary/40">:</span>
        <TimeBox value={parts.seconds} label="sec" />
      </div>
      <div className="text-[11px] text-muted-foreground">Ends {endLabel} · 23:59 IST</div>
    </div>
  );
}

function TimeBox({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex flex-col items-center">
      <span className="text-2xl font-extrabold leading-none">{String(value).padStart(2, "0")}</span>
      <span className="mt-0.5 text-[9px] uppercase tracking-wider text-muted-foreground">{label}</span>
    </div>
  );
}
